import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  School as SchoolIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationOnIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import CertificateDialog from '../components/CertificateDialog';

interface Certificate {
  _id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userDepartment: string;
  userOffice?: string;
  userPosition: string;
  courseId: string;
  courseName: string;
  courseCategory: string;
  completionDate: string;
  position: string;
  score: number;
  certificateIssued: boolean;
  certificateIssuedAt: string | null;
}

const CompanyCertificates: React.FC = () => {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [offices, setOffices] = useState<any[]>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
  const [certificateData, setCertificateData] = useState<any>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchCertificates();
    fetchOffices();
  }, []);

  const fetchOffices = async () => {
    try {
      const officesList = await api.getOffices();
      setOffices(officesList.filter((office: any) => office.isActive));
    } catch (err: any) {
      console.error('Error cargando sedes:', err);
      setOffices([]);
    }
  };

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const data = await api.getCompanyCertificates();
      setCertificates(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando certificados');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCertificate = async (certificate: Certificate) => {
    try {
      setGenerating(true);
      // Generar los datos del certificado
      const certificateData = {
        studentName: certificate.userName,
        courseName: certificate.courseName,
        completionDate: certificate.completionDate,
        position: certificate.position,
        score: certificate.score,
        company: {
          name: user?.company?.name || 'Empresa',
          logo: user?.company?.logo || null
        }
      };
      setCertificateData(certificateData);
      setSelectedCertificate(certificate);
      setCertificateDialogOpen(true);
    } catch (err: any) {
      console.error('Error generando certificado:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadCertificate = async (certificate: Certificate) => {
    await handleViewCertificate(certificate);
  };

  // Filtrar certificados
  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = 
      cert.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.userEmail.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCourse = !courseFilter || cert.courseName === courseFilter;
    const matchesEmployee = !employeeFilter || cert.userName === employeeFilter;
    const matchesOffice = !officeFilter || cert.userOffice === officeFilter;

    return matchesSearch && matchesCourse && matchesEmployee && matchesOffice;
  });

  // Obtener listas únicas para los filtros
  const uniqueCourses = Array.from(new Set(certificates.map(c => c.courseName))).sort();
  const uniqueEmployees = Array.from(new Set(certificates.map(c => c.userName))).sort();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Cargando certificados...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Certificados de Empleados
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filtros */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Buscar"
                placeholder="Buscar por empleado, curso o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm('')}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Filtrar por Curso</InputLabel>
                <Select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                  label="Filtrar por Curso"
                >
                  <MenuItem value="">Todos los cursos</MenuItem>
                  {uniqueCourses.map(course => (
                    <MenuItem key={course} value={course}>{course}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Filtrar por Sede</InputLabel>
                <Select
                  value={officeFilter}
                  onChange={(e) => setOfficeFilter(e.target.value)}
                  label="Filtrar por Sede"
                >
                  <MenuItem value="">Todas las sedes</MenuItem>
                  {offices.map(office => (
                    <MenuItem key={office._id} value={office.name}>{office.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Filtrar por Empleado</InputLabel>
                <Select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  label="Filtrar por Empleado"
                >
                  <MenuItem value="">Todos los empleados</MenuItem>
                  {uniqueEmployees.map(employee => (
                    <MenuItem key={employee} value={employee}>{employee}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {/* Tabla de certificados */}
        <Card>
          <CardContent>
            {filteredCertificates.length === 0 ? (
              <Box textAlign="center" py={4}>
                <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No se encontraron certificados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {certificates.length === 0 
                    ? 'Aún no hay certificados emitidos para los empleados de la empresa.'
                    : 'No hay certificados que coincidan con los filtros seleccionados.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Empleado</strong></TableCell>
                      <TableCell><strong>Curso</strong></TableCell>
                      <TableCell><strong>Sede</strong></TableCell>
                      <TableCell><strong>Departamento</strong></TableCell>
                      <TableCell><strong>Fecha de Completado</strong></TableCell>
                      <TableCell><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCertificates.map((certificate) => (
                      <TableRow key={certificate._id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon color="action" fontSize="small" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {certificate.userName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {certificate.userEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <SchoolIcon color="action" fontSize="small" />
                            <Typography variant="body2">
                              {certificate.courseName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {certificate.userOffice && certificate.userOffice !== 'N/A' && (
                              <LocationOnIcon color="action" fontSize="small" />
                            )}
                            <Typography variant="body2">
                              {certificate.userOffice || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={certificate.userDepartment}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarIcon color="action" fontSize="small" />
                            <Typography variant="body2">
                              {formatDate(certificate.completionDate)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Descargar Certificado">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownloadCertificate(certificate)}
                              disabled={generating}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Diálogo de certificado */}
      {certificateData && selectedCertificate && (
        <CertificateDialog
          open={certificateDialogOpen}
          onClose={() => {
            setCertificateDialogOpen(false);
            setCertificateData(null);
            setSelectedCertificate(null);
          }}
          studentName={certificateData.studentName}
          courseName={certificateData.courseName}
          completionDate={certificateData.completionDate}
          position={certificateData.position}
          companyName={certificateData.company?.name}
          companyLogo={certificateData.company?.logo}
          certificateSigner={certificateData.company?.certificateSigner}
          showCTOSignature={certificateData.company?.showCTOSignature ?? true}
          hideShareButtons={true}
          allowViewCertificates={user?.company?.settings?.allowViewCertificates ?? true}
        />
      )}
    </Container>
  );
};

export default CompanyCertificates;

